import React, { useEffect, useState } from 'react';
import { Modal, Spin, message } from 'antd';
import { formatFullTime } from '../../../utils/timeFormat';
import { REGION_OPTIONS } from '../packages/packageFormOptions';
import { PLAN_PERIOD_LABEL } from '../packages/packageManageData';
import {
  MANAGED_FULFILLMENT_STATUS_LABEL,
  MANAGED_ORDER_STATUS_LABEL,
  MANAGED_ORDER_TYPE_LABEL,
  MANAGED_PAYMENT_CHANNEL_LABEL,
  MANAGED_PAYMENT_RECORD_STATUS_LABEL,
  MANAGED_PAYMENT_STATUS_LABEL,
  formatMinorAmount,
  getConsoleOrderDetail,
  type ManagedOrder,
  type ManagedOrderDetail,
  type ManagedOrderFeatureDescription,
  type ManagedPayment
} from './orderManageData';

type OrderDetailModalProps = {
  open: boolean;
  orderNo: string | null;
  onClose: () => void;
};

function dash(value?: string | number | null) {
  if (value == null) return '—';
  const text = String(value).trim();
  return text ? text : '—';
}

function formatOrderTime(value?: string | null) {
  const text = formatFullTime(value);
  return text || '—';
}

function labelOf(map: Record<string, string>, value?: string) {
  if (!value) return '—';
  return map[value] || value;
}

function regionText(regionCode?: string) {
  if (!regionCode) return '—';
  const label = REGION_OPTIONS.find((item) => item.value === regionCode)?.label;
  return label ? `${label} ${regionCode}` : regionCode;
}

function money(amountMinor?: number, fractionDigits?: number, currency?: string) {
  if (amountMinor == null) return '—';
  const amount = formatMinorAmount(amountMinor, fractionDigits);
  const code = currency?.trim();
  return code ? `${amount} ${code}` : amount;
}

function dayText(value?: number | null) {
  if (value == null) return '—';
  return `${value}天`;
}

function featureText(item: ManagedOrderFeatureDescription) {
  const title = item.title?.trim() || '—';
  if (item.displayType === 'BOOL') return `${title} ${item.supported ? '支持' : '不支持'}`;
  if (item.displayType === 'QUOTA') {
    const period = PLAN_PERIOD_LABEL[item.period ?? ''] || item.period || '';
    return `${title} ${item.quota ?? '—'}${item.unit?.trim() || ''}${period ? `/${period}` : ''}`;
  }
  if (item.displayType === 'TEXT') return `${title} ${item.text?.trim() || '—'}`;
  return title;
}

function Cell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className='admin-order-detail-cell'>
      <span className='admin-order-detail-label'>{label}</span>
      <span className='admin-order-detail-value'>{value}</span>
    </div>
  );
}

function PaymentBlock({ item, index }: { item: ManagedPayment; index: number }) {
  return (
    <div className='admin-order-detail-grid'>
      <Cell label='序号' value={index + 1} />
      <Cell label='渠道' value={labelOf(MANAGED_PAYMENT_CHANNEL_LABEL, item.channel)} />
      <Cell label='场景' value={dash(item.scene)} />
      <Cell label='状态' value={labelOf(MANAGED_PAYMENT_RECORD_STATUS_LABEL, item.status)} />
      <Cell label='商户交易号' value={dash(item.merchantTradeNo)} />
      <Cell label='渠道交易号' value={dash(item.providerTransactionId)} />
      <Cell label='金额' value={money(item.amountMinor, item.currencyFractionDigits, item.currency)} />
      <Cell label='创建时间' value={formatOrderTime(item.createdAt)} />
      <Cell label='更新时间' value={dash(item.updatedAt)} />
    </div>
  );
}

function OrderBody({ detail }: { detail: ManagedOrderDetail }) {
  const order: ManagedOrder = detail.order ?? {};
  const features = (detail.featureDescriptions ?? []).map(featureText).filter(Boolean);
  const payments = detail.payments ?? [];

  return (
    <div className='admin-order-detail-body'>
      <section className='admin-order-detail-card'>
        <div className='admin-order-detail-card-title'>基本信息</div>
        <div className='admin-order-detail-grid'>
          <Cell label='订单号' value={dash(order.orderNo)} />
          <Cell label='订单状态' value={labelOf(MANAGED_ORDER_STATUS_LABEL, order.orderStatus)} />
          <Cell label='订单类型' value={labelOf(MANAGED_ORDER_TYPE_LABEL, order.orderType)} />
          <Cell label='用户编码' value={dash(order.userCode)} />
          <Cell label='用户名称' value={dash(order.userName)} />
          <Cell label='手机号' value={dash(order.phone)} />
          <Cell label='邮箱' value={dash(order.email)} />
          <Cell label='地区' value={regionText(order.regionCode)} />
          <Cell label='商品名称' value={dash(order.productName)} />
          <Cell label='数量' value={order.quantity == null ? '—' : order.quantity} />
          <Cell label='套餐天数' value={dayText(order.durationDays)} />
          <Cell label='权益天数' value={dayText(order.validityDays)} />
          <Cell label='单价' value={money(detail.unitAmountMinor, order.currencyFractionDigits, order.currency)} />
          <Cell label='订单金额' value={money(order.amountMinor, order.currencyFractionDigits, order.currency)} />
          <Cell label='创建时间' value={formatOrderTime(order.createdAt)} />
          <Cell label='更新时间' value={formatOrderTime(detail.updatedAt)} />
          <Cell label='报价失效' value={formatOrderTime(detail.quoteExpiresAt)} />
          <Cell label='关闭时间' value={formatOrderTime(order.closedAt)} />
        </div>
      </section>

      <section className='admin-order-detail-card'>
        <div className='admin-order-detail-card-title'>功能说明</div>
        <div className='admin-order-detail-value'>{features.length ? features.join('；') : '暂无功能说明'}</div>
      </section>

      <section className='admin-order-detail-card'>
        <div className='admin-order-detail-card-title'>支付信息</div>
        <div className='admin-order-detail-grid'>
          <Cell label='支付状态' value={labelOf(MANAGED_PAYMENT_STATUS_LABEL, order.paymentStatus)} />
          <Cell label='发放状态' value={labelOf(MANAGED_FULFILLMENT_STATUS_LABEL, order.fulfillmentStatus)} />
          <Cell label='支付渠道' value={labelOf(MANAGED_PAYMENT_CHANNEL_LABEL, order.paymentChannel)} />
          <Cell label='支付时间' value={formatOrderTime(order.paidAt)} />
        </div>
        {payments.length ? (
          payments.map((item, index) => (
            <div key={`${item.merchantTradeNo ?? ''}-${item.createdAt ?? ''}-${index}`} style={{ marginTop: 12 }}>
              <PaymentBlock item={item} index={index} />
            </div>
          ))
        ) : (
          <div className='admin-order-detail-value' style={{ marginTop: 12 }}>
            暂无支付记录
          </div>
        )}
      </section>

      {detail.fulfillment ? (
        <section className='admin-order-detail-card'>
          <div className='admin-order-detail-card-title'>发放进度</div>
          <div className='admin-order-detail-grid'>
            <Cell label='任务状态' value={dash(detail.fulfillment.status)} />
            <Cell label='执行次数' value={detail.fulfillment.attemptCount == null ? '—' : detail.fulfillment.attemptCount} />
            <Cell label='下次执行' value={dash(detail.fulfillment.nextRunAt)} />
          </div>
        </section>
      ) : null}

      {detail.gift ? (
        <section className='admin-order-detail-card'>
          <div className='admin-order-detail-card-title'>赠送信息</div>
          <div className='admin-order-detail-grid'>
            <Cell label='管理员编码' value={dash(detail.gift.adminCode)} />
            <Cell label='管理员名称' value={dash(detail.gift.adminName)} />
            <Cell label='赠送原因' value={dash(detail.gift.reason)} />
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function OrderDetailModal({ open, orderNo, onClose }: OrderDetailModalProps) {
  const [detail, setDetail] = useState<ManagedOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !orderNo) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    getConsoleOrderDetail(orderNo)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || '订单详情加载失败');
          setDetail(null);
          return;
        }
        setDetail(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error('订单详情加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orderNo]);

  return (
    <Modal
      title='订单详情'
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={960}
      className='admin-order-detail-modal admin-order-detail-scroll'
      footer={null}
    >
      {loading ? (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <Spin />
        </div>
      ) : detail ? (
        <OrderBody detail={detail} />
      ) : null}
    </Modal>
  );
}
